/**
 * RecurrenceEditionController — page /factures-recurrence-edition.html
 * (EPIC-014.4).
 *
 * Édition d'une récurrence existante (status `active` ou `paused`).
 * Charge l'existant via `GET /recurring-invoices/{id}`, calcule en
 * temps réel les 3 prochaines dates via `POST
 * /recurring-invoices/preview-dates` à chaque changement de cadence,
 * soumet via `PATCH /recurring-invoices/{id}`.
 *
 * Validation client-side :
 *  - intervalMonths ∈ [1, 12]
 *  - dayOfMonth ∈ [1, 28]
 *  - endDate, si renseigné, > startDate
 *
 * Erreurs serveur 422 (`details.fields`) rendues sous chaque champ via
 * `aria-describedby` + `role="alert"`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

export default class RecurrenceEditionController extends BaseController {
  static targets = [
    "form",
    "clientField",
    "intervalMonths",
    "dayOfMonth",
    "startDate",
    "endDate",
    "lines",
    "generationMode",
    "paymentProvider",
    "reminders",
    "attachmentsMode",
    "previewDatesList",
    "submitBtn",
    "readonlyBanner",
    "crumbDetailLink",
  ];

  connect() {
    const params = new URLSearchParams(window.location.search);
    this._recurringId = params.get("id");
    if (!this._recurringId) {
      this.showError("Identifiant de récurrence manquant.");
      return;
    }
    this._load();
  }

  async _load() {
    try {
      this._detail = await api.get(`/recurring-invoices/${encodeURIComponent(this._recurringId)}`);
      this._hydrateForm(this._detail);
      this._updateCrumbLink(this._recurringId);
      this._renderReadonlyIfNeeded(this._detail.status);
    } catch (err) {
      this.showError(err instanceof APIError ? err.message : "Erreur de chargement.");
    }
  }

  _hydrateForm(detail) {
    if (this.hasClientFieldTarget) this.clientFieldTarget.value = detail.clientId || "";
    if (this.hasIntervalMonthsTarget) this.intervalMonthsTarget.value = String(detail.intervalMonths);
    if (this.hasDayOfMonthTarget) this.dayOfMonthTarget.value = String(detail.dayOfMonth);
    if (this.hasStartDateTarget) this.startDateTarget.value = detail.startDate || "";
    if (this.hasEndDateTarget) this.endDateTarget.value = detail.endDate || "";
    if (this.hasPaymentProviderTarget) this.paymentProviderTarget.checked = Boolean(detail.paymentProviderEnabled);
    if (this.hasRemindersTarget) this.remindersTarget.checked = Boolean(detail.remindersEnabled);

    const mode = detail.generationMode || "draft";
    this.generationModeTargets.forEach((radio) => {
      radio.checked = radio.value === mode;
    });

    const attMode = detail.attachmentsMode || "recalculate";
    this.attachmentsModeTargets.forEach((radio) => {
      radio.checked = radio.value === attMode;
    });

    this.previewDates();
  }

  _renderReadonlyIfNeeded(status) {
    const editable = status === "active" || status === "paused";
    if (this.hasReadonlyBannerTarget) {
      this.readonlyBannerTarget.hidden = editable;
      if (!editable) {
        this.readonlyBannerTarget.textContent =
          `Cette récurrence est en statut « ${status} » et ne peut plus être éditée.`;
      }
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = !editable;
    }
  }

  _updateCrumbLink(id) {
    if (this.hasCrumbDetailLinkTarget) {
      this.crumbDetailLinkTarget.setAttribute("href", `/factures-recurrence-detail.html?id=${encodeURIComponent(id)}`);
      this.crumbDetailLinkTarget.textContent = "Récurrence";
    }
  }

  async previewDates() {
    const payload = this._buildCadencePayload();
    if (!payload) {
      if (this.hasPreviewDatesListTarget) this.previewDatesListTarget.replaceChildren();
      return;
    }
    try {
      const response = await api.post("/recurring-invoices/preview-dates", payload);
      this._renderPreviewDates(response.dates || []);
    } catch (err) {
      this._renderPreviewDates([]);
    }
  }

  _buildCadencePayload() {
    const intervalMonths = Number(this.intervalMonthsTarget?.value);
    const dayOfMonth = Number(this.dayOfMonthTarget?.value);
    const startDate = this.startDateTarget?.value;
    if (!Number.isFinite(intervalMonths) || intervalMonths < 1 || intervalMonths > 12) return null;
    if (!Number.isFinite(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 28) return null;
    if (!startDate) return null;
    const payload = { intervalMonths, dayOfMonth, startDate };
    if (this.endDateTarget?.value) payload.endDate = this.endDateTarget.value;
    return payload;
  }

  _renderPreviewDates(dates) {
    if (!this.hasPreviewDatesListTarget) return;
    this.previewDatesListTarget.replaceChildren();
    if (dates.length === 0) {
      const p = document.createElement("p");
      p.className = "muted fs-13";
      p.textContent = "Aucune date à prévoir (cadence invalide ou date de fin atteinte).";
      this.previewDatesListTarget.appendChild(p);
      return;
    }
    const ul = document.createElement("ul");
    ul.style.listStyle = "none";
    ul.style.padding = "0";
    ul.style.margin = "0";
    for (const date of dates) {
      const li = document.createElement("li");
      li.className = "fs-13";
      li.style.padding = "4px 0";
      li.textContent = this.formatShortDate(date) || date;
      ul.appendChild(li);
    }
    this.previewDatesListTarget.appendChild(ul);
  }

  addLine() {
    if (!this.hasLinesTarget) return;
    const fragment = this.cloneTemplate("tpl-recurrence-line");
    if (!fragment) return;
    this.linesTarget.appendChild(fragment);
  }

  removeLine(event) {
    const row = event.currentTarget.closest("tr");
    if (row) row.remove();
  }

  async submit(event) {
    event?.preventDefault();
    this._clearFieldErrors();

    const payload = this._collectPayload();
    const clientErrors = this._validateClientSide(payload);
    if (clientErrors.length > 0) {
      this._renderFieldErrors(clientErrors);
      this.showError("Veuillez corriger les erreurs avant d'enregistrer.");
      return;
    }

    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;
    try {
      const response = await api.patch(
        `/recurring-invoices/${encodeURIComponent(this._recurringId)}`,
        payload,
      );
      this.showSuccess("Récurrence mise à jour.");
      window.location.href = `/factures-recurrence-detail.html?id=${encodeURIComponent(response.id)}`;
    } catch (err) {
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;
      const serverFields = err?.data?.error?.details?.fields;
      if (err instanceof APIError && Array.isArray(serverFields)) {
        this._renderFieldErrors(serverFields);
        this.showError(err.message || "Champs invalides.");
      } else {
        this.showError(err instanceof APIError ? err.message : "Erreur lors de l'enregistrement.");
      }
    }
  }

  _collectPayload() {
    const intervalMonths = Number(this.intervalMonthsTarget?.value);
    const dayOfMonth = Number(this.dayOfMonthTarget?.value);
    const mode = this.generationModeTargets.find((r) => r.checked)?.value;
    const attMode = this.attachmentsModeTargets.find((r) => r.checked)?.value;
    const payload = {
      clientId: this.clientFieldTarget?.value || null,
      intervalMonths,
      dayOfMonth,
      startDate: this.startDateTarget?.value || null,
      endDate: this.endDateTarget?.value || null,
      generationMode: mode || "draft",
      paymentProviderEnabled: Boolean(this.paymentProviderTarget?.checked),
      remindersEnabled: Boolean(this.remindersTarget?.checked),
      attachmentsMode: attMode || "recalculate",
      lines: this._collectLines(),
    };
    if (!payload.endDate) delete payload.endDate;
    return payload;
  }

  _collectLines() {
    if (!this.hasLinesTarget) return [];
    const lines = [];
    this.linesTarget.querySelectorAll("tr").forEach((row) => {
      const label = row.querySelector('[name="line-label"]')?.value?.trim();
      const quantity = row.querySelector('[name="line-quantity"]')?.value;
      const unitPrice = row.querySelector('[name="line-unit-price"]')?.value;
      if (!label) return;
      lines.push({
        label,
        quantity: Number(quantity) || 1,
        unitPriceExclVat: unitPrice || "0",
      });
    });
    return lines;
  }

  _validateClientSide(payload) {
    const errors = [];
    if (!payload.clientId) {
      errors.push({ field: "clientId", message: "Veuillez sélectionner un client." });
    }
    if (!Number.isFinite(payload.intervalMonths) || payload.intervalMonths < 1 || payload.intervalMonths > 12) {
      errors.push({ field: "intervalMonths", message: "La périodicité doit être comprise entre 1 et 12 mois." });
    }
    if (!Number.isFinite(payload.dayOfMonth) || payload.dayOfMonth < 1 || payload.dayOfMonth > 28) {
      errors.push({ field: "dayOfMonth", message: "Le jour du mois doit être compris entre 1 et 28." });
    }
    if (!payload.startDate) {
      errors.push({ field: "startDate", message: "La date de début est requise." });
    }
    if (payload.endDate && payload.startDate && payload.endDate <= payload.startDate) {
      errors.push({ field: "endDate", message: "La date de fin doit être postérieure à la date de début." });
    }
    return errors;
  }

  _renderFieldErrors(errors) {
    for (const err of errors) {
      const targetName = err.field;
      if (!targetName) continue;
      const camel = targetName.charAt(0).toUpperCase() + targetName.slice(1);
      const targetGetter = `has${camel}Target`;
      const target = this[targetGetter] ? this[`${targetName}Target`] : null;
      if (!target) continue;
      const errorId = `${target.id || targetName}-error`;
      let errorEl = document.getElementById(errorId);
      if (!errorEl) {
        errorEl = document.createElement("p");
        errorEl.id = errorId;
        errorEl.className = "nm-field__error fs-12";
        errorEl.setAttribute("role", "alert");
        errorEl.setAttribute("aria-live", "polite");
        target.setAttribute("aria-describedby", errorId);
        target.parentElement?.appendChild(errorEl);
      }
      errorEl.textContent = err.message || "Champ invalide.";
    }
  }

  _clearFieldErrors() {
    this.element.querySelectorAll(".nm-field__error").forEach((node) => node.remove());
  }
}
